/**
 * Slide renderer.
 *
 * Each slide returns a `Split` with `teach` (always) and an optional
 * `practise` block. When `practise` is set we render a two-pane
 * layout (teach left, practise right on desktop; stacked on mobile);
 * otherwise teach takes full width.
 */

import type { Slide, Unit } from '../types';

interface Split {
  teach: string;
  practise?: string;
}

export function renderSlide(root: HTMLElement, unit: Unit, slide: Slide): void {
  void unit;
  root.replaceChildren();
  // five-ways uses a wide landscape layout instead of the centred narrow
  // column used by every other slide type. The router clears layout
  // classes on each render; we re-add the right one here.
  root.classList.toggle('slide--landscape', slide.type === 'five-ways');
  root.append(buildShell(slide, renderSplit(slide)));
}

function buildShell(slide: Slide, split: Split): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'slide-shell';
  wrapper.dataset.slideType = slide.type;
  if (split.practise) wrapper.dataset.layout = 'split';

  // five-ways slides carry their own two-column headers; the generic
  // tags + eyebrow slide-head is noise on those.
  const head =
    slide.type === 'five-ways'
      ? ''
      : `
        <header class="slide-head">
          <div class="tags">${slide.tags.map((tag) => `<span class="tag">${tag}</span>`).join('')}</div>
          <p class="eyebrow">${escapeHtml(slide.title)}</p>
        </header>
      `;
  const body = split.practise
    ? `
      <div class="slide-split">
        <section class="slide-teach" aria-label="Learn">${split.teach}</section>
        <section class="slide-practise" aria-label="Practise">${split.practise}</section>
      </div>
    `
    : `<div class="slide-single">${split.teach}</div>`;

  wrapper.innerHTML = `
    ${head}
    ${body}
    ${slide.caption ? `<p class="caption">${escapeHtml(slide.caption)}</p>` : ''}
    ${slide.note ? `<p class="note">${escapeHtml(slide.note)}</p>` : ''}
  `;
  return wrapper;
}

// --- Per-type splits --------------------------------------------------------

function renderSplit(slide: Slide): Split {
  switch (slide.type) {
    case 'five-ways':
      return {
        teach: fiveWaysBody(slide),
      };

    case 'phrase':
      return {
        teach: phraseTeach(slide.phrase.text, slide.phrase.ipa, slide.instruction),
      };

    case 'chorus-repeat': {
      const rounds = slide.rounds ?? 3;
      return {
        teach: phraseTeach(slide.phrase.text, slide.phrase.ipa, `Repeat ${rounds} times.`),
      };
    }

    case 'info':
      return {
        teach: `
          <div class="card">
            <p>${escapeHtml(slide.body)}</p>
            ${list(slide.bullets)}
          </div>
        `,
        practise: practiseConfirm({
          label: "I'm ready",
          prompt: slide.instruction ?? 'Read carefully, then tap when ready.',
        }),
      };

    case 'dialogue':
      return {
        teach: `
          <div class="dialogue">
            ${slide.lines
              .map(
                (line) =>
                  `<p class="dialogue-line"><span class="who">${line.who}</span><span>${escapeHtml(line.text)}</span></p>`
              )
              .join('')}
          </div>
        `,
      };

    case 'sound-focus':
      return {
        teach: `
          <div class="sound">
            <h1 class="sound-symbol">${escapeHtml(slide.symbol)}</h1>
            ${slide.mouthCue ? `<p class="sound-cue">${escapeHtml(slide.mouthCue)}</p>` : ''}
            <div class="sound-examples">
              ${slide.examples.map((ex) => `<span class="sound-example">${escapeHtml(ex)}</span>`).join('')}
            </div>
          </div>
        `,
      };

    case 'stress':
      return {
        teach: `
          <div>
            <h1 class="phrase">${escapeHtml(slide.stressPattern)}</h1>
            ${slide.description ? `<p class="phrase-sub">${escapeHtml(slide.description)}</p>` : ''}
            ${list(slide.examples)}
          </div>
        `,
      };

    case 'listen-choose':
      return {
        teach: `
          <div>
            <h1 class="slide-title">${escapeHtml(slide.question)}</h1>
            <p class="phrase-sub">Read each option carefully and choose.</p>
          </div>
        `,
        practise: `
          <div class="practise-card">
            ${slide.instruction ? `<p class="practise-instruction">${escapeHtml(slide.instruction)}</p>` : ''}
            <div class="choices">
              ${slide.choices
                .map(
                  (choice) =>
                    `<button class="choice" type="button" data-correct="${choice.correct ? 'true' : 'false'}" aria-pressed="false">${escapeHtml(choice.label)}</button>`
                )
                .join('')}
            </div>
          </div>
        `,
      };

    case 'tokens':
      return {
        teach: `
          <div>
            <div class="grid-tokens">
              ${slide.tokens.map((token) => `<span class="token">${escapeHtml(token)}</span>`).join('')}
            </div>
          </div>
        `,
      };

    case 'ask-answer':
      return {
        teach: `
          <div>
            <h1 class="slide-title">${escapeHtml(slide.question)}</h1>
            <p class="phrase-sub">Example: <em>${escapeHtml(slide.exampleAnswer)}</em></p>
          </div>
        `,
      };

    case 'record':
      return {
        teach: `
          <div>
            <h1 class="slide-title">${escapeHtml(slide.prompt)}</h1>
            ${slide.hint ? `<p class="phrase-sub">${escapeHtml(slide.hint)}</p>` : ''}
          </div>
        `,
      };

    case 'self-check':
      return {
        teach: `
          <div>
            <h1 class="slide-title">Quick self-check</h1>
            <p class="phrase-sub">Honestly tick what you can do.</p>
          </div>
        `,
        practise: `
          <div class="practise-card">
            <div class="cando-list">
              ${slide.items.map((item) => `<label class="cando-item"><input type="checkbox" /> <span>${escapeHtml(item)}</span></label>`).join('')}
            </div>
          </div>
        `,
      };

    case 'review':
      return {
        teach: `
          <div>
            <h1 class="slide-title">Quick review</h1>
            <p class="phrase-sub">Read each prompt and answer in your head.</p>
            ${list(slide.prompts)}
          </div>
        `,
        practise: practiseConfirm({
          label: 'I remembered these',
          prompt: slide.instruction ?? 'Tick when you have answered all of them.',
        }),
      };

    case 'capstone':
      return {
        teach: `
          <div class="capstone">
            <p class="capstone-eyebrow">Capstone</p>
            <h1 class="phrase">${escapeHtml(slide.prompt)}</h1>
          </div>
        `,
      };

    default:
      return exhaustive(slide);
  }
}

// --- Reusable building blocks ----------------------------------------------

function fiveWaysBody(slide: import('../types').FiveWaysSlide): string {
  const rows = slide.ways
    .map(
      (way, i) => `
        <tr>
          <td class="col-num"><span class="num-badge" aria-hidden="true">${i + 1}</span></td>
          <td class="col-en">
            <span class="five-way-en-stack">
              <span class="five-way-text">${escapeHtml(way.english)}</span>${way.ipa ? `<span class="five-way-ipa">${escapeHtml(way.ipa)}</span>` : ''}
            </span>
          </td>
          <td class="col-ar" dir="rtl" lang="ar">${escapeHtml(way.darija)}</td>
        </tr>
      `
    )
    .join('');
  const warning = slide.warning
    ? `<aside class="five-ways-warning" dir="rtl" lang="ar">
         <span class="five-ways-warning-icon" aria-hidden="true">!</span>
         <p>${escapeHtml(slide.warning.darija)}</p>
       </aside>`
    : '';
  const chatgptPrompt = buildChatGPTPrompt(slide);
  const chatgpt = `
    <aside class="five-ways-chatgpt">
      <header class="five-ways-chatgpt-header">
        <span class="five-ways-chatgpt-icon" aria-hidden="true">
          <svg viewBox="0 0 24 24" width="22" height="22"><path d="M12 3a3 3 0 0 0-3 3v6a3 3 0 0 0 6 0V6a3 3 0 0 0-3-3zM5 11a7 7 0 0 0 14 0M12 18v3" stroke="currentColor" stroke-width="1.75" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </span>
        <p class="five-ways-chatgpt-title">Practice with ChatGPT (voice mode)</p>
      </header>
      <pre class="five-ways-chatgpt-prompt">${escapeHtml(chatgptPrompt)}</pre>
      <div class="five-ways-chatgpt-actions">
        <button type="button" class="five-ways-chatgpt-btn five-ways-chatgpt-btn--ghost" data-copy-prompt>
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><rect x="9" y="9" width="11" height="11" rx="2" stroke="currentColor" stroke-width="1.75" fill="none"/><path d="M5 15V5a2 2 0 0 1 2-2h10" stroke="currentColor" stroke-width="1.75" fill="none" stroke-linecap="round"/></svg>
          <span class="cta-label">Copy</span>
        </button>
        <button type="button" class="five-ways-chatgpt-btn five-ways-chatgpt-btn--primary" data-open-chatgpt>
          <span>Open ChatGPT</span>
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
      </div>
    </aside>
  `;
  return `
    <section class="five-ways">
      <h1 class="five-ways-concept">${escapeHtml(slide.concept)}</h1>
      <div class="five-ways-table-wrap">
        <table class="five-ways-table">
          <thead>
            <tr>
              <th scope="col" class="col-num">#</th>
              <th scope="col" class="col-en">English</th>
              <th scope="col" class="col-ar" dir="rtl" lang="ar">بالدارجة</th>
            </tr>
          </thead>
          <tbody>${rows}</tbody>
        </table>
      </div>
      ${warning}
      ${chatgpt}
    </section>
  `;
}

function buildChatGPTPrompt(slide: import('../types').FiveWaysSlide): string {
  const phrases = slide.ways
    .map((way, i) => `${i + 1}. ${way.english}${way.ipa ? ` ${way.ipa}` : ''}`)
    .join('\n');
  return [
    `You are a friendly English tutor helping a Moroccan beginner. Speak ONLY in English — never Arabic, French, or Darija. Speak naturally, like you would to any friend.`,
    ``,
    `Today's topic: ${slide.concept}`,
    ``,
    `The 5 phrases the learner just studied:`,
    phrases,
    ``,
    `Please turn on voice mode and:`,
    `1. Greet me using one of the 5 phrases above.`,
    `2. Ask me a simple, short question.`,
    `3. Wait for me to reply, then gently correct any mistakes and praise me ("Good job!" / "Nice!").`,
    `4. Practice each of the 5 phrases naturally over 3–4 short exchanges.`,
    `5. When I am doing well, say: "Great work — you can move on to the next slide." Then stop.`,
    ``,
    `Rules:`,
    `- English only. If I speak Arabic, ask me to try again in English.`,
    `- Speak at a normal, natural pace.`,
    `- Praise often and warmly.`,
    ``,
    `Begin now.`,
  ].join('\n');
}

function phraseTeach(text: string, ipa: string | undefined, sub?: string): string {
  return `
    <div>
      <h1 class="phrase">${escapeHtml(text)}</h1>
      ${ipa ? `<p class="phrase-sub">${escapeHtml(ipa)}</p>` : ''}
      ${sub ? `<p class="phrase-meta">${escapeHtml(sub)}</p>` : ''}
    </div>
  `;
}

function practiseConfirm(opts: { label: string; prompt?: string | undefined }): string {
  return `
    <div class="practise-card">
      ${opts.prompt ? `<p class="practise-instruction">${escapeHtml(opts.prompt)}</p>` : ''}
      <button class="practice-chip" type="button" data-complete-slide aria-pressed="false">${escapeHtml(opts.label)}</button>
    </div>
  `;
}

function list(items: string[] | undefined): string {
  if (!items?.length) return '';
  return `<ul class="bullet-list">${items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>`;
}

// --- Interaction wiring -----------------------------------------------------

export function wireSlideInteractions(root: HTMLElement, onComplete: () => void): void {
  root.addEventListener('click', (event) => {
    const target = event.target as Element | null;

    const copyBtn = target?.closest<HTMLButtonElement>('[data-copy-prompt]');
    if (copyBtn && root.contains(copyBtn)) {
      void handleCopyPrompt(copyBtn);
      return;
    }

    const openBtn = target?.closest<HTMLButtonElement>('[data-open-chatgpt]');
    if (openBtn && root.contains(openBtn)) {
      handleOpenChatGPT(openBtn);
      return;
    }

    const choice = target?.closest<HTMLButtonElement>('.choice');
    if (choice && root.contains(choice)) {
      const choices = choice.parentElement?.querySelectorAll<HTMLButtonElement>('.choice') ?? [];
      for (const button of choices) {
        button.setAttribute('aria-pressed', String(button === choice));
        button.dataset.state = button.dataset.correct === 'true' ? 'correct' : button === choice ? 'incorrect' : '';
      }
      if (choice.dataset.correct === 'true') onComplete();
      return;
    }

    const completeButton = target?.closest<HTMLButtonElement>('[data-complete-slide]');
    if (completeButton && root.contains(completeButton)) {
      completeButton.setAttribute('aria-pressed', 'true');
      completeButton.dataset.state = 'complete';
      completeButton.textContent = 'Done';
      onComplete();
    }
  });

  root.addEventListener('change', (event) => {
    const checkbox = (event.target as Element | null)?.closest<HTMLInputElement>('.cando-item input');
    if (!checkbox || !root.contains(checkbox)) return;
    const checkboxes = checkbox.closest('.cando-list')?.querySelectorAll<HTMLInputElement>('input') ?? [];
    if (checkboxes.length > 0 && Array.from(checkboxes).every((input) => input.checked)) onComplete();
  });
}

function escapeHtml(value: string): string {
  return value.replace(/[&<>"]/g, (ch) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[ch]!);
}

function readPromptText(scope: Element): string {
  const aside = scope.closest('.five-ways-chatgpt');
  return aside?.querySelector('.five-ways-chatgpt-prompt')?.textContent?.trim() ?? '';
}

async function handleCopyPrompt(button: HTMLButtonElement): Promise<void> {
  const text = readPromptText(button);
  if (!text) return;

  const label = button.querySelector<HTMLElement>('.cta-label');
  const originalLabel = label?.textContent ?? '';
  try {
    await navigator.clipboard.writeText(text);
    if (label) label.textContent = 'Copied!';
    button.dataset.state = 'copied';
  } catch {
    if (label) label.textContent = 'Copy failed';
  }

  setTimeout(() => {
    if (label) label.textContent = originalLabel;
    delete button.dataset.state;
  }, 2000);
}

// Opens ChatGPT with the prompt injected via the `?prompt=` query
// param — ChatGPT reads it and pre-fills the conversation, so the
// learner doesn't have to paste anything.
function handleOpenChatGPT(button: HTMLButtonElement): void {
  const text = readPromptText(button);
  if (!text) return;
  const url = `https://chatgpt.com/?prompt=${encodeURIComponent(text)}`;
  window.open(url, '_blank', 'noopener,noreferrer');
}

function exhaustive(value: never): never {
  throw new Error(`Unhandled slide type: ${JSON.stringify(value)}`);
}
